/**
 * Diverging stance charts (SPEC §3.03 / §3.08): counts centred on a stated
 * neutral position, opposed left, supportive right, and unscored records in
 * their OWN column after a hairline — never a stack segment. Plus the
 * technology × source heatmap, which shows COUNTS in an ink ramp (a mean of
 * ≤4 statements scored ±1 would fake agreement).
 */
import React from 'react';
import type { ReportTheme } from '../../../../../types/reportTheme';
import { roleColor } from '../../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../../lib/reportSectionHeading';
import type { PsrSourceCoverageState } from '../../../../../lib/sentiment/publicSentimentMetaV2Contract';
import {
  PSR_SCOPE_LABELS,
  psrHeatBin,
  psrHeatmap,
  psrPartialCaveat,
  psrSourceLabel,
  psrStancedCount,
  psrTechLabel,
  psrTechRows,
  psrTechsNotQueried,
  psrReasonLabel,
  type PsrReportView,
} from '../../../../../lib/sentiment/psrReportView';
import {
  CoverageMark,
  PSR_APPARATUS,
  PSR_CHART_WIDTH,
  PSR_SERIF,
  PsrBlock,
  PsrLegend,
  PsrNote,
  PsrSwatch,
  PsrTable,
  StanceLegend,
  psrPalette,
} from './psrUi';

/* ── Geometry helpers ──────────────────────────────────────────────────── */

/** A horizontal bar with optional 4px rounded data-ends. */
export function barPath(x0: number, x1: number, y: number, h: number, roundLeft: boolean, roundRight: boolean): string {
  const w = Math.max(0, x1 - x0);
  const r = Math.min(4, w / 2, h / 2);
  const L = roundLeft ? r : 0;
  const R = roundRight ? r : 0;
  const f = (n: number) => n.toFixed(2);
  return [
    `M${f(x0 + L)},${f(y)}`,
    `L${f(x1 - R)},${f(y)}`,
    R ? `A${f(R)},${f(R)} 0 0 1 ${f(x1)},${f(y + R)} L${f(x1)},${f(y + h - R)} A${f(R)},${f(R)} 0 0 1 ${f(x1 - R)},${f(y + h)}` : `L${f(x1)},${f(y + h)}`,
    `L${f(x0 + L)},${f(y + h)}`,
    L ? `A${f(L)},${f(L)} 0 0 1 ${f(x0)},${f(y + h - L)} L${f(x0)},${f(y + L)} A${f(L)},${f(L)} 0 0 1 ${f(x0 + L)},${f(y)}` : `L${f(x0)},${f(y)}`,
    'Z',
  ].join(' ');
}

/** Smallest "nice" step giving ≤3 ticks per side; returns the axis max and ticks. */
export function niceAxis(max: number): { max: number; ticks: number[] } {
  if (!(max > 0)) return { max: 0, ticks: [] };
  const steps = [1, 2, 3, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000];
  const step = steps.find(s => Math.ceil(max / s) <= 3) ?? Math.ceil(max / 3);
  const n = Math.ceil(max / step);
  return { max: n * step, ticks: Array.from({ length: n }, (_, i) => (i + 1) * step) };
}

export interface DivergingRow {
  key: string;
  label: string;
  sublabel?: string;
  opposed: number;
  neutral: number;
  supportive: number;
  unscored: number;
  /** Context rows: thinner, muted, never labelled as stance counts. */
  thin?: boolean;
  /** Printed at the centre when the row has no assessed statement. */
  noStanceText?: string;
  /** Draws a labelled full-width rule above this row. */
  dividerLabel?: string;
}

const CENTER = 284;
const HALF = 136;
const DIVIDER_X = 436;
const UNSCORED_X = 452;
const UNSCORED_MAX_W = PSR_CHART_WIDTH - UNSCORED_X - 36;

/** Shared diverging stacked bar with the separate unscored column. */
export function DivergingStanceChart({ theme, rows, ariaLabel, testId }: { theme: ReportTheme; rows: DivergingRow[]; ariaLabel: string; testId: string }): React.ReactElement {
  const p = psrPalette(theme);
  const muted = roleColor(theme, 'muted');
  const faint = roleColor(theme, 'faint');
  const rule = roleColor(theme, 'rule');
  const ink = roleColor(theme, 'ink');
  const extent = Math.max(0, ...rows.map(r => Math.max(r.opposed, r.supportive) + r.neutral / 2));
  const axis = niceAxis(extent);
  const unit = axis.max > 0 ? HALF / axis.max : 0;
  const maxUnscored = Math.max(0, ...rows.map(r => r.unscored));
  const uUnit = maxUnscored > 0 ? Math.min(unit || Infinity, UNSCORED_MAX_W / maxUnscored) : 0;
  const head = { fontFamily: PSR_APPARATUS, fontSize: 12, fontWeight: 600, letterSpacing: 1 } as const;
  const tick = { fontFamily: PSR_APPARATUS, fontSize: 12, fontVariantNumeric: 'tabular-nums' } as const;

  let cursor = 24;
  const laid = rows.map(row => {
    let dividerY: number | undefined;
    if (row.dividerLabel) { dividerY = cursor; cursor += 24; }
    const top = cursor;
    cursor += row.thin ? 36 : 44;
    return { row, top, dividerY };
  });
  const bottom = cursor + 2;
  const height = bottom + 22;

  return (
    <svg data-testid={testId} viewBox={`0 0 ${PSR_CHART_WIDTH} ${height}`} width="100%" role="img" aria-label={ariaLabel} style={{ display: 'block', maxWidth: '100%' }}>
      <text x={CENTER - HALF} y="12" fill={muted} style={head}>OPPOSED</text>
      <text x={CENTER} y="12" textAnchor="middle" fill={muted} style={head}>NEUTRAL</text>
      <text x={CENTER + HALF} y="12" textAnchor="end" fill={muted} style={head}>SUPPORTIVE</text>
      <text x={UNSCORED_X} y="12" fill={muted} style={head}>UNSCORED</text>
      {axis.ticks.flatMap(t => [-t, t]).map(t => (
        <g key={t}>
          <line x1={CENTER + t * unit} y1="24" x2={CENTER + t * unit} y2={bottom} stroke={rule} strokeWidth="1" strokeDasharray="2 4" />
          <text x={CENTER + t * unit} y={bottom + 15} textAnchor="middle" fill={muted} style={tick}>{Math.abs(t)}</text>
        </g>
      ))}
      <line x1={CENTER} y1="20" x2={CENTER} y2={bottom} stroke={muted} strokeWidth="1" />
      <text x={CENTER} y={bottom + 15} textAnchor="middle" fill={muted} style={tick}>0</text>
      <line x1={DIVIDER_X} y1="2" x2={DIVIDER_X} y2={bottom} stroke={rule} strokeWidth="1" />
      {laid.map(({ row, top, dividerY }) => {
        const h = row.thin ? 10 : 16;
        const y = top + (row.thin ? 15 : 14);
        const labelY = top + (row.thin ? 18 : 20);
        const stanced = row.opposed + row.neutral + row.supportive;
        const n0 = CENTER - (row.neutral * unit) / 2;
        const n1 = CENTER + (row.neutral * unit) / 2;
        const o0 = n0 - row.opposed * unit;
        const s1 = n1 + row.supportive * unit;
        const segs: Array<{ k: string; x0: number; x1: number; fill: string; text: string; value: number; labelled: boolean }> = [];
        if (row.opposed > 0) segs.push({ k: 'o', x0: o0, x1: row.neutral > 0 ? n0 - 1 : CENTER - 1, fill: p.opposed, text: p.onOpposed, value: row.opposed, labelled: true });
        if (row.neutral > 0) segs.push({ k: 'n', x0: row.opposed > 0 ? n0 + 1 : n0, x1: row.supportive > 0 ? n1 - 1 : n1, fill: p.neutral, text: p.onNeutral, value: row.neutral, labelled: false });
        if (row.supportive > 0) segs.push({ k: 's', x0: row.neutral > 0 ? n1 + 1 : CENTER + 1, x1: s1, fill: p.supportive, text: p.onSupportive, value: row.supportive, labelled: true });
        const uW = row.unscored * uUnit;
        return (
          <g key={row.key} data-diverging-row={row.key} data-opposed={row.opposed} data-neutral={row.neutral} data-supportive={row.supportive} data-unscored={row.unscored}>
            {dividerY !== undefined && (
              <>
                <text x="0" y={dividerY + 14} fill={faint} style={{ fontFamily: PSR_APPARATUS, fontSize: 11, fontWeight: 600, letterSpacing: 1.1 }}>{row.dividerLabel!.toUpperCase()}</text>
                <line x1="0" y1={dividerY + 20.5} x2={PSR_CHART_WIDTH} y2={dividerY + 20.5} stroke={rule} strokeWidth="1" />
              </>
            )}
            <text x="0" y={labelY} fill={row.thin ? muted : ink} style={{ fontFamily: PSR_SERIF, fontSize: 13, fontWeight: row.thin ? 500 : 600 }}>{row.label}</text>
            {row.sublabel && <text x="0" y={labelY + 15} fill={faint} style={{ fontFamily: PSR_APPARATUS, fontSize: 11 }}>{row.sublabel}</text>}
            {stanced === 0 && row.noStanceText && (
              <text x={CENTER} y={y + h - 3} textAnchor="middle" fill={faint} style={{ fontFamily: PSR_SERIF, fontSize: 12, fontStyle: 'italic' }}>{row.noStanceText}</text>
            )}
            {segs.map((seg, i) => (
              <g key={seg.k} data-segment={seg.k}>
                <path d={barPath(seg.x0, seg.x1, y, h, i === 0, i === segs.length - 1)} fill={seg.fill}>
                  <title>{`${row.label}: ${seg.value} ${seg.k === 'o' ? 'opposed' : seg.k === 'n' ? 'neutral statement' : 'supportive'}`}</title>
                </path>
                {seg.labelled && !row.thin && seg.x1 - seg.x0 >= 18 && (
                  <text x={(seg.x0 + seg.x1) / 2} y={y + 12} textAnchor="middle" fill={seg.text} style={{ fontFamily: PSR_APPARATUS, fontSize: 11.5, fontWeight: 600 }}>{seg.value}</text>
                )}
              </g>
            ))}
            {row.unscored > 0 && (
              <g data-segment="u">
                <path d={barPath(UNSCORED_X, UNSCORED_X + uW, y, h, false, true)} fill={p.unscored}>
                  <title>{`${row.label}: ${row.unscored} unscored`}</title>
                </path>
                <text x={UNSCORED_X + uW + 6} y={y + h - (row.thin ? 1 : 4)} fill={ink} style={{ fontFamily: PSR_APPARATUS, fontSize: 12, fontWeight: 600 }}>{row.unscored}</text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/* ── Local stance by technology ────────────────────────────────────────── */

export function TechStanceTable({ theme, view, caption }: { theme: ReportTheme; view: PsrReportView; caption?: React.ReactNode }): React.ReactElement | null {
  const rows = psrTechRows(view.v2);
  if (!rows.length) return null;
  const site = view.site;
  return (
    <PsrTable
      theme={theme}
      testId="psr-tech-stance-table"
      caption={caption}
      columns={[
        { header: 'Technology', width: '34%' },
        { header: 'Opposed', align: 'right', numeric: true },
        { header: 'Neutral', align: 'right', numeric: true },
        { header: 'Supportive', align: 'right', numeric: true },
        { header: 'Unscored', align: 'right', numeric: true },
        { header: 'Records', align: 'right', numeric: true },
      ]}
      groups={[{
        key: 'rows',
        rows: [
          ...rows.map(row => ({ key: row.btmTech, cells: [row.label, row.opposed, row.neutralStated, row.supportive, row.unscored, row.total] })),
          ...(site ? [{ key: 'total', cells: ['Local total', site.opposed ?? 0, site.neutralStated ?? 0, site.supportive ?? 0, site.unscored, site.total] }] : []),
        ],
      }]}
    />
  );
}

/** Chapter 03 chart: one row per selected technology, site scope only. */
export function TechStanceBars({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  if (view.index === undefined) return null;
  const rows = psrTechRows(view.v2);
  if (!rows.length) return null;
  const notQueried = psrTechsNotQueried(view.techsQueried.length ? view.techsQueried : rows.map(r => r.btmTech));
  return (
    <PsrBlock
      theme={theme}
      testId="psr-tech-stance-bars"
      title="Local stance by technology"
      subtitle="Assessed local statements, centered on neutral · unscored records in their own column"
      partial={view.partial}
      caveat={psrPartialCaveat(view, 'Bars count what was read.')}
      table={<TechStanceTable theme={theme} view={view} />}
    >
      <StanceLegend theme={theme} />
      <DivergingStanceChart
        theme={theme}
        testId="psr-tech-stance-chart"
        ariaLabel="Local stance by technology, diverging stacked bar"
        rows={rows.map(row => ({
          key: row.btmTech,
          label: row.label,
          sublabel: `${row.stanced} assessed of ${row.total} local`,
          opposed: row.opposed,
          neutral: row.neutralStated,
          supportive: row.supportive,
          unscored: row.unscored,
          noStanceText: 'No assessed statements',
        }))}
      />
      <PsrNote theme={theme}>
        Counts of dated, explicit statements at site scope. A neutral segment is a stated neutral position.
        {notQueried.length > 0 && ` ${notQueried.join(' and ')} ${notQueried.length === 1 ? 'was' : 'were'} not queried for this site.`}
        {' '}Statewide and national records appear under Local stance and regional context.
      </PsrNote>
    </PsrBlock>
  );
}

/* ── Where local stance was found (heatmap) ────────────────────────────── */

function coverageFor(view: PsrReportView, sourceId: string): PsrSourceCoverageState | undefined {
  return view.coverage.find(row => row.sourceId === sourceId);
}

export function TechSourceTable({ theme, view, caption }: { theme: ReportTheme; view: PsrReportView; caption?: React.ReactNode }): React.ReactElement | null {
  const map = psrHeatmap(view.v2);
  if (!map.techs.length || !map.sources.length) return null;
  const cellText = (cell: { assessedLocal: number; siteRows: number } | undefined) => (cell ? `${cell.assessedLocal} of ${cell.siteRows}` : '—');
  return (
    <PsrTable
      theme={theme}
      testId="psr-tech-source-table"
      caption={caption}
      columns={[
        { header: 'Technology', width: '26%' },
        ...map.sources.map(source => ({ header: psrSourceLabel(source), align: 'right' as const, numeric: true })),
        { header: 'Total', align: 'right', numeric: true },
      ]}
      groups={[{
        key: 'rows',
        rows: [
          ...map.techs.map(tech => ({ key: tech, cells: [psrTechLabel(tech), ...map.sources.map(source => cellText(map.cell(tech, source))), cellText(map.rowTotal(tech))] })),
          { key: 'total', cells: ['Total', ...map.sources.map(source => cellText(map.colTotal(source))), cellText(map.total)] },
        ],
      }]}
    />
  );
}

/** Chapter 03 heatmap: assessed local statements of all local records per cell. */
export function TechSourceHeatmap({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  if (view.index === undefined) return null;
  const map = psrHeatmap(view.v2);
  if (!map.techs.length || !map.sources.length) return null;
  const p = psrPalette(theme);
  const ink = roleColor(theme, 'ink');
  const muted = roleColor(theme, 'muted');
  const faint = roleColor(theme, 'faint');
  const hair = roleColor(theme, 'hair');
  const rule = roleColor(theme, 'rule');
  const maxCell = Math.max(0, ...map.techs.flatMap(tech => map.sources.map(source => map.cell(tech, source)?.assessedLocal ?? 0)));
  const binColors = [p.empty, p.amount[1], p.amount[2], p.amount[3]] as const;
  const binText = [p.onEmpty, p.onAmount[1], p.onAmount[2], p.onAmount[3]] as const;
  const key = (label: string, fill: string, text: string, border?: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', width: 28, height: 18, borderRadius: 2, background: fill, border: border ? `1px solid ${border}` : undefined, fontFamily: PSR_APPARATUS, fontSize: 11, fontWeight: 600, color: text }}>{label}</span>
  );
  const headLabel: React.CSSProperties = { fontFamily: PSR_APPARATUS, fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: muted, lineHeight: 1.35 };
  const totalCell = (cell: { assessedLocal: number; siteRows: number }, size: number) => (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 4, fontFamily: PSR_APPARATUS, fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ fontSize: size, fontWeight: 600, color: ink }}>{cell.assessedLocal}</span>
      <span style={{ fontSize: 11, color: muted }}>of {cell.siteRows}</span>
    </span>
  );
  return (
    <PsrBlock
      theme={theme}
      testId="psr-tech-source-heatmap"
      title="Where local stance was found"
      subtitle="Technology × source · assessed local statements, of all local records in the cell"
      partial={view.partial}
      caveat="A low count in an unreported or partial column may be a coverage gap, not an absence."
      table={<TechSourceTable theme={theme} view={view} />}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
        <span style={{ fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, fontWeight: 500, color: roleColor(theme, 'body') }}>Assessed local statements per cell</span>
        {key('0', binColors[0], binText[0])}
        {key('1', binColors[1], binText[1])}
        {key('2', binColors[2], binText[2])}
        {key('3+', binColors[3], binText[3])}
        <span aria-hidden="true" style={{ width: 1, height: 14, background: rule, flex: 'none' }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: PSR_APPARATUS, fontSize: REPORT_TEXT_PX.label, color: roleColor(theme, 'body') }}>
          {key('—', 'transparent', ink, hair)}No local records
        </span>
      </div>
      <table data-testid="psr-heatmap-grid" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 2, tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: 136 }} />
          {map.sources.map(source => <col key={source} />)}
          <col style={{ width: 76 }} />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" style={{ textAlign: 'left', verticalAlign: 'bottom', paddingBottom: 6, ...headLabel }}>Technology</th>
            {map.sources.map(source => {
              const coverage = coverageFor(view, source);
              return (
                <th key={source} scope="col" style={{ textAlign: 'left', verticalAlign: 'bottom', padding: '0 0 6px 2px', fontWeight: 400 }}>
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontFamily: PSR_SERIF, fontSize: 12.64, fontWeight: 600, color: ink, lineHeight: 1.25 }}>{psrSourceLabel(source)}</span>
                    {coverage && <CoverageMark theme={theme} state={coverage.coverage} fontSize={11} weight={500} ink="muted" />}
                  </span>
                </th>
              );
            })}
            <th scope="col" style={{ textAlign: 'right', verticalAlign: 'bottom', paddingBottom: 6, ...headLabel }}>All sources</th>
          </tr>
        </thead>
        <tbody>
          {map.techs.map(tech => (
            <tr key={tech}>
              <th scope="row" style={{ textAlign: 'left', fontFamily: PSR_SERIF, fontSize: 13, fontWeight: 600, color: ink, lineHeight: 1.25 }}>{psrTechLabel(tech)}</th>
              {map.sources.map(source => {
                const cell = map.cell(tech, source);
                if (!cell) {
                  return (
                    <td key={source} data-heat-cell={`${tech}|${source}`} data-heat-bin="none" style={{ height: 54, boxSizing: 'border-box', border: `1px solid ${hair}`, textAlign: 'center', verticalAlign: 'middle' }}>
                      <span style={{ display: 'block', fontFamily: PSR_APPARATUS, fontSize: 15, fontWeight: 500, color: faint, lineHeight: 1.1 }}>—</span>
                      <span style={{ display: 'block', fontFamily: PSR_APPARATUS, fontSize: 11, color: faint }}>no local records</span>
                    </td>
                  );
                }
                const bin = psrHeatBin(cell.assessedLocal);
                return (
                  <td key={source} data-heat-cell={`${tech}|${source}`} data-heat-bin={bin} title={`${psrTechLabel(tech)} · ${psrSourceLabel(source)}: ${cell.assessedLocal} assessed of ${cell.siteRows} local`} style={{ height: 54, background: binColors[bin], textAlign: 'center', verticalAlign: 'middle' }}>
                    <span style={{ display: 'block', fontFamily: PSR_APPARATUS, fontSize: 17, fontWeight: 600, color: binText[bin], lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>{cell.assessedLocal}</span>
                    <span style={{ display: 'block', fontFamily: PSR_APPARATUS, fontSize: 11, color: binText[bin] }}>of {cell.siteRows} local</span>
                  </td>
                );
              })}
              <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>{totalCell(map.rowTotal(tech), 15)}</td>
            </tr>
          ))}
          <tr>
            <th scope="row" style={{ textAlign: 'left', borderTop: `1px solid ${rule}`, paddingTop: 8, ...headLabel }}>All technologies</th>
            {map.sources.map(source => (
              <td key={source} style={{ textAlign: 'center', borderTop: `1px solid ${rule}`, paddingTop: 8 }}>{totalCell(map.colTotal(source), 13)}</td>
            ))}
            <td style={{ textAlign: 'right', borderTop: `1px solid ${rule}`, paddingTop: 8 }}>{totalCell(map.total, 15)}</td>
          </tr>
        </tbody>
      </table>
      <PsrNote theme={theme}>
        Counts, not means: a cell holds at most {maxCell} {maxCell === 1 ? 'statement' : 'statements'} scored −1, 0 or +1, so a mean would fake agreement. Direction is in the technology bar. “of N local” counts every site-scope record from that source, scored or not.
      </PsrNote>
    </PsrBlock>
  );
}

/* ── Local stance and regional context ─────────────────────────────────── */

export function ScopeStanceTable({ theme, view, caption }: { theme: ReportTheme; view: PsrReportView; caption?: React.ReactNode }): React.ReactElement | null {
  const scopes = view.v2.scopeCounts ?? [];
  if (!scopes.length) return null;
  const assessed = view.index !== undefined;
  return (
    <PsrTable
      theme={theme}
      testId="psr-scope-stance-table"
      caption={caption}
      columns={assessed ? [
        { header: 'Scope', width: '30%' },
        { header: 'Opposed', align: 'right', numeric: true },
        { header: 'Neutral', align: 'right', numeric: true },
        { header: 'Supportive', align: 'right', numeric: true },
        { header: 'Unscored', align: 'right', numeric: true },
        { header: 'Records', align: 'right', numeric: true },
      ] : [
        { header: 'Scope', width: '40%' },
        { header: 'Unscored', align: 'right', numeric: true },
        { header: 'Records', align: 'right', numeric: true },
      ]}
      groups={[{
        key: 'rows',
        rows: scopes.map(row => ({
          key: row.scope,
          cells: assessed
            ? [PSR_SCOPE_LABELS[row.scope], row.opposed ?? 0, row.neutralStated ?? 0, row.supportive ?? 0, row.unscored, row.total]
            : [PSR_SCOPE_LABELS[row.scope], row.unscored, row.total],
        })),
      }]}
    />
  );
}

/** The local unscored breakdown line under the no-stance bar: "2 agenda · 1 first-seen". */
function localReasonBreakdown(view: PsrReportView): string {
  return (view.v2.unscoredByReason ?? []).filter(row => row.local > 0).map(row => `${row.local} ${psrReasonLabel(row.reasonCode).toLowerCase()}`).join(' · ');
}

/** Chapter 08: local stance vs statewide and national context. */
export function LocalVsContextBars({ theme, view }: { theme: ReportTheme; view: PsrReportView }): React.ReactElement | null {
  if (view.state === 'coverage-only' || !view.site) return null;
  const site = view.site;
  const context = [view.stateScope, view.country].filter((row): row is NonNullable<typeof row> => !!row);
  if (view.index !== undefined) {
    return (
      <PsrBlock
        theme={theme}
        testId="psr-local-vs-context"
        title="Local stance and regional context"
        subtitle="Assessed statements by scope, centered on neutral · unscored in their own column"
        partial={view.partial}
        caveat={psrPartialCaveat(view, 'The local row counts what was read.')}
        table={<ScopeStanceTable theme={theme} view={view} />}
      >
        <StanceLegend theme={theme} />
        <DivergingStanceChart
          theme={theme}
          testId="psr-local-vs-context-chart"
          ariaLabel="Stance by scope: local versus statewide and national context"
          rows={[
            { key: 'site', label: PSR_SCOPE_LABELS.site, sublabel: `${psrStancedCount(site)} assessed of ${site.total} records`, opposed: site.opposed ?? 0, neutral: site.neutralStated ?? 0, supportive: site.supportive ?? 0, unscored: site.unscored },
            ...context.map((row, i) => ({
              key: row.scope, label: PSR_SCOPE_LABELS[row.scope], sublabel: `${psrStancedCount(row)} assessed of ${row.total} records`,
              opposed: row.opposed ?? 0, neutral: row.neutralStated ?? 0, supportive: row.supportive ?? 0, unscored: row.unscored, thin: true,
              ...(i === 0 ? { dividerLabel: 'Context, not local stance' } : {}),
            })),
          ]}
        />
        <PsrNote theme={theme}>
          Context rows are thinner and sit below the rule; they describe the wider debate, not people near the site. A per-scope mean is never plotted: it would average unscored rows at zero weight.
        </PsrNote>
      </PsrBlock>
    );
  }
  // No dated local stance: records only — no stance is charted or tallied.
  const p = psrPalette(theme);
  const muted = roleColor(theme, 'muted');
  const breakdown = localReasonBreakdown(view);
  const uW = Math.min(200, site.unscored * 15);
  return (
    <PsrBlock
      theme={theme}
      testId="psr-local-vs-context"
      title="Local stance and regional context"
      subtitle="Records by scope · unscored in their own column"
      partial={view.partial}
      caveat={psrPartialCaveat(view, 'The local row counts what was read.')}
      table={<ScopeStanceTable theme={theme} view={view} />}
    >
      <PsrLegend theme={theme} items={[{ key: 'unscored', swatch: <PsrSwatch color={p.unscored} />, label: 'Unscored (not in the index)' }]} />
      <div data-testid="psr-local-no-stance" style={{ display: 'flex', alignItems: 'stretch', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 148px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
          <span style={{ fontFamily: PSR_SERIF, fontSize: 13, fontWeight: 600, color: roleColor(theme, 'ink') }}>{PSR_SCOPE_LABELS.site}</span>
          <span style={{ fontFamily: PSR_APPARATUS, fontSize: 11, color: roleColor(theme, 'faint') }}>0 assessed of {site.total} records</span>
        </div>
        <div style={{ flex: '1 1 160px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <span style={{ fontFamily: PSR_APPARATUS, fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: muted }}>Assessed statements</span>
          <span style={{ fontFamily: PSR_APPARATUS, fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: muted }}>No dated local stance</span>
        </div>
        <div style={{ flex: '1 1 200px', borderLeft: `1px solid ${roleColor(theme, 'rule')}`, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontFamily: PSR_APPARATUS, fontSize: 12, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: muted }}>Unscored</span>
          {site.unscored > 0 && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ display: 'block', width: uW, height: 16, background: p.unscored, borderRadius: '0 4px 4px 0' }} />
              <span style={{ fontFamily: PSR_APPARATUS, fontSize: 12, fontWeight: 600, color: roleColor(theme, 'ink') }}>{site.unscored}</span>
            </span>
          )}
          {breakdown && <span style={{ fontFamily: PSR_APPARATUS, fontSize: 11, color: roleColor(theme, 'faint') }}>{breakdown}</span>}
        </div>
      </div>
      {context.length > 0 && (
        <>
          <div style={{ fontFamily: PSR_APPARATUS, fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: roleColor(theme, 'faint') }}>Context, not local stance</div>
          <PsrTable
            theme={theme}
            testId="psr-context-records-table"
            columns={[{ header: 'Scope', width: '32%' }, { header: 'Records', align: 'right', numeric: true, width: '16%' }, { header: 'Use in this report' }]}
            groups={[{ key: 'ctx', rows: context.map(row => ({ key: row.scope, cells: [PSR_SCOPE_LABELS[row.scope], row.total, 'Listed in the evidence inventory; not charted'] })) }]}
          />
        </>
      )}
      <PsrNote theme={theme}>
        Records only. Without a local stance, statewide and national direction is not charted or tallied, so it cannot stand in for the omitted chapters.
      </PsrNote>
    </PsrBlock>
  );
}
